import React, { memo } from "react";
import { Link } from "react-router-dom";
import "./nextBestActionCard.css";

function normalizeActionClassName(value, fallback = "btn btn--ghost") {
  const raw = String(value || fallback).trim() || fallback;

  const mapped = raw
    .split(/\s+/)
    .filter(Boolean)
    .map((token) => {
      switch (token) {
        case "btn-primary":
          return "btn--primary";
        case "btn-ghost":
          return "btn--ghost";
        case "btn-sm":
          return "btn--sm";
        case "btn-outline":
          return "btn--outline";
        case "btn-danger":
          return "btn--danger";
        case "btn-success":
          return "btn--success";
        case "btn-soft":
          return "btn--soft";
        default:
          return token;
      }
    });

  if (!mapped.includes("btn")) {
    mapped.unshift("btn");
  }

  return [...new Set(mapped)].join(" ");
}

function handleActionClick({ disabled, onClick, isLink }) {
  return (e) => {
    if (disabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (typeof onClick === "function") {
      onClick(e);
    }

    if (!isLink && e.defaultPrevented) {
      return;
    }
  };
}

function renderAction(action, key, defaultClassName) {
  if (!action || !action.label) return null;

  const {
    label,
    to,
    onClick,
    disabled = false,
    className = defaultClassName,
    type = "button",
  } = action;

  const finalClassName = normalizeActionClassName(className, defaultClassName);

  const clickHandler = handleActionClick({
    disabled,
    onClick,
    isLink: Boolean(to),
  });

  if (to) {
    return (
      <Link
        key={key}
        to={disabled ? "#" : to}
        className={finalClassName}
        aria-disabled={disabled ? "true" : undefined}
        tabIndex={disabled ? -1 : undefined}
        onClick={clickHandler}
      >
        {label}
      </Link>
    );
  }

  return (
    <button
      key={key}
      type={type}
      className={finalClassName}
      onClick={clickHandler}
      disabled={disabled}
    >
      {label}
    </button>
  );
}

function NextBestActionCard({
  eyebrow = "Next step",
  icon = null,
  title,
  description = "",
  note = "",
  primaryAction = null,
  secondaryAction = null,
}) {
  if (!title) return null;

  return (
    <section className="nba-card" aria-label={eyebrow || "Next step"}>
      <div className="nba-top">
        <div className="nba-copy">
          {eyebrow ? <div className="nba-eyebrow">{eyebrow}</div> : null}

          <div className="nba-titleRow">
            {icon ? (
              <div className="nba-icon" aria-hidden="true">
                {icon}
              </div>
            ) : null}

            <h3 className="nba-title">{title}</h3>
          </div>

          {description ? (
            <p className="nba-description">{description}</p>
          ) : null}

          {note ? <div className="nba-note">{note}</div> : null}
        </div>
      </div>

      {primaryAction || secondaryAction ? (
        <div className="nba-actions">
          {primaryAction
            ? renderAction(primaryAction, "nba-primary", "btn btn--primary")
            : null}

          {secondaryAction
            ? renderAction(secondaryAction, "nba-secondary", "btn btn--ghost")
            : null}
        </div>
      ) : null}
    </section>
  );
}

NextBestActionCard.displayName = "NextBestActionCard";

export default memo(NextBestActionCard);